import React from 'react';
// template components
import BulletedList from '@nmx/template/dist/componentLibrary/components/BulletedList';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// styles
import { LinkStyled } from './styles';

export default () => (
  <SectionContainer
    id='hipaa-privacy-notice-ltc'
    backgroundVariant='lightA'
    themeType='lightTheme'>
    <Row align='center'>
      <Col medium={8}>
        <Typography
          component='h1'
          nextElement='typography'>
          Notice of Privacy Practices for Protected Health Information of Northwestern Long Term Care <WidowBlock>Insurance Company</WidowBlock>
        </Typography>
        <Typography nextElement='link'>
          This notice describes how medical information about you may be used and disclosed and how you can get access to this information. Please review it carefully.
        </Typography>
        <LinkStyled
          id='hipaa-privacy-notice-ltc-printable-link'
          variant='primaryDeemphasize'
          linkUrl='/assets/pdf/legal/nltc-hipaa.pdf'
          ariaLabel='Medical Information Notice'
          isInternalPdf>
          Printable Version
        </LinkStyled>
        <Typography nextElement='typography'>
          To help you understand how we protect your health information, this Notice of Privacy Practices &#40;the &#34;Notice&#34;&#41;
          describes the current privacy policy and practices of Northwestern Long Term Care Insurance Company &#40;the
          &#34;Company&#34;&#41; for health information.
        </Typography>
        <Typography nextElement='typography'>
          The Company will use and share protected health information of Insureds as necessary to carry out payment and
          health care operations as permitted by law. We are required by law to maintain the privacy of our Insureds&#39;
          protected health information and to provide Insureds with notice of our legal duties and privacy practices with
          respect to their protected health information. We are required to abide by the terms of this Notice so long as
          it remains in effect. We reserve the right to change the terms of this Notice and to make the new Notice
          effective for all protected health information maintained by us. Copies of any revised notices will be mailed to
          all Insureds then covered by the Company.
        </Typography>
        <Typography
          component='h2'
          align='center'
          nextElement='typography'>
          Uses and Disclosures of Your Protected Health Information
        </Typography>
        <Typography nextElement='typography'>
          This section describes uses and disclosures of your protected health information that we may make. In some
          states, more stringent laws may limit or prohibit a use or disclosure described below. In those circumstances,
          the Company will conduct itself according to the more stringent law.
        </Typography>
        {/* tier 1 */}
        <BulletedList
          bulletedListData={[
            {
              bulletText: <><strong>Your Authorization</strong>&#8212;Except as described in this Notice, we will not use or disclose your
                protected health information, including psychotherapy notes, without written authorization from you. In
                addition, use or disclosure of protected health information for marketing purposes, or disclosures of
                protected health information in a manner that constitutes a sale, require your authorization. If you do
                authorize the Company to use or disclose your protected health information for another purpose, you may
                revoke your authorization in writing at any time. If you revoke an authorization, the Company will no longer
                use or disclose your protected health information in the manner covered by that authorization, except to the
                extent that the Company has taken action in reliance on the authorization, or if the authorization was
                obtained as a condition of obtaining insurance coverage, the Company has the right to contest a claim under
                a policy or to contest the policy itself.</>,
            },
            {
              bulletText: <><strong>Uses and Disclosures for Payment</strong>&#8212;The Company will make uses and disclosures of your
                protected health information as necessary and as permitted by law for payment purposes. For example, we may
                use information regarding your medical procedures and treatment to process and pay claims or to determine
                whether services are covered under your long&#45;term care insurance plan. The Company may also forward such
                information to another health plan, which may also have an obligation to process and pay claims on your behalf.</>,
            },
            {
              bulletText: <><strong>Uses and Disclosures for Health Care Operations</strong>&#8212;The Company will use and disclose your
                protected health information as necessary, and as permitted by law, for our health care operations. This
                includes enrollment, underwriting, policy issuance, securing reinsurance, customer service, and other
                activities relating to the creation and servicing of your insurance coverage, compliance, auditing, rating,
                fraud and abuse detection, business management and general administrative activities, quality improvement
                and assurance, and other functions related to your long&#45;term care insurance policy. Such activities may
                involve our use of third parties that perform services for us. When we hire other parties to help us conduct
                our business, we require them to protect your protected health information. Further, we do not permit them
                to use or share your protected health information for any purpose other than the work they are doing on our
                behalf or as required by law. In addition, your Northwestern Mutual Financial Representative&#42; and others
                assisting your Financial Representative have access to the information that they need to provide service to you.</>,
            },
            {
              bulletText: <><strong>Disclosures to an Employer for Premium Payment Purposes</strong>&#8212;The Company may disclose to your
                employer when necessary for premium payment purposes only: your name, address, policy number, and premium
                amount due for your long&#45;term care insurance policy. Be assured that the Company will not disclose any other
                protected health information to your employer without your written authorization.</>,
            },
            {
              bulletText: <><strong>Family and Friends Involved in Your Care</strong>&#8212;With your approval, the Company may from time to
                time disclose your protected health information to designated family, friends, and others who are involved
                in your care or in payment for your care. Such disclosures are limited to the information necessary to
                facilitate that person&#39;s involvement in caring for you or paying for your care. If you are unavailable,
                incapacitated, or facing an emergency medical situation, and we determine that a limited disclosure may be
                in your best interest, we may share limited protected health information with such individuals without your
                approval. If you have designated a person &#40;i.e., secondary addressee&#41; to receive information regarding
                payment of the premium on your long&#45;term care policy, we will inform that person when your premium has not
                been paid. We may also disclose limited protected health information to a public or private entity that is
                authorized to assist in disaster relief efforts in order for that entity to locate a family member or other
                person that may be involved in some aspect of caring for you.</>,
            },
            {
              bulletText: <>
                <strong>Payment of Claims</strong>&#8212;We may contact you and&#47;or your authorized representative to provide information on payment of your claims.
                {/* tier 2 */}
                <BulletedList
                  bulletType='disc'
                  bulletedListData={[
                    {
                      bulletText: <>
                        <strong>Other Uses and Disclosures</strong>&#8212;We are permitted or required by law to make certain other uses and disclosures of your protected health information without your authorization. We may release your protected health information:
                        {/* tier 3 */}
                        <BulletedList
                          bulletType='filledSquare'
                          bulletedListData={[
                            { bulletText: <>for public health activities, such as required reporting of disease, injury, birth and death, and for required public health investigations&#59;</> },
                            { bulletText: <>as required by law if we believe you to be a victim of abuse, neglect, or domestic violence&#59;</> },
                            { bulletText: <>if required by law to a government oversight agency conducting audits, investigations, or civil or criminal proceedings&#59;</> },
                            { bulletText: <>if required to do so by a court or administrative subpoena or discovery request&#59; in most cases you will have notice of such release&#59;</> },
                            { bulletText: <>to law enforcement officials as required by law to report wounds and injuries and crimes&#59;</> },
                            { bulletText: <>to law enforcement agencies to help prevent fraud, or alert them that unlawful activity may have occurred&#59;</> },
                            { bulletText: <>if you are a member of the military as required by armed forces services&#59;</> },
                            { bulletText: <>if necessary for national security or intelligence activities&#59;</> },
                            { bulletText: <>to insurance and other regulatory authorities&#59;</> },
                            { bulletText: <>to workers&#39; compensation agencies if necessary for your workers&#39; compensation benefit determination&#59;</> },
                            { bulletText: <>to our reinsurers&#59;</> },
                            { bulletText: <>to your attending physician or medical professional or facility to enable them to inform you of medical information of which you may not be aware&#59; and</> },
                            { bulletText: <>to others as permitted or required by law.</> },
                          ]}
                        />
                      </>,
                    },
                  ]}
                />
              </>,
            },
          ]}
        />
        <Typography
          component='h3'
          align='center'
          nextElement='typography'>
          Rights That You Have
        </Typography>
        <Typography nextElement='typography'>
          You have a number of rights related to your protected health information that are described below. All
          communication and requests regarding those rights, where applicable, should be submitted in writing, signed by
          you or your personal representative and mailed to our Privacy Official at the address listed at the end of this Notice.
        </Typography>
        <BulletedList
          bulletedListData={[
            {
              bulletText: <><strong>Access to Your Protected Health Information</strong>&#8212;You have the right to copy and&#47;or inspect
                protected health information in certain records that we retain on your behalf, including your application,
                billing and benefit statements, claim forms, policy change requests, and records relating to your health or
                medical condition or treatment. We may charge you a reasonable, cost&#45;based fee for any copies you request.
                We may also charge for postage if you request a mailed copy and will charge for preparing a summary of the
                requested information if you request one.</>,
            },
            {
              bulletText: <><strong>Amendments to Your Protected Health Information</strong>&#8212;You have the right to request that certain
                protected health information that we maintain about you be amended or corrected. We are not obligated to
                make all requested amendments but will give each request careful consideration. All amendment requests must
                state the reasons for the amendment&#47;correction request. If we make an amendment or correction you request,
                we may also notify others who work with us and have copies of the uncorrected record if we believe that such
                notification is necessary. Please understand that we will not amend protected health information that we did
                not create unless we are notified of the need for amendment by the entity that created it. For example,
                requests to amend information in your medical records need to be directed to the medical provider or
                facility that created the information.</>,
            },
            {
              bulletText: <><strong>Accounting for Disclosures of Your Protected Health Information</strong>&#8212;You have the right to
                receive an accounting of certain disclosures we make of your protected health information after April 14,
                2003. The first accounting in any 12&#45;month period is free&#59; you may be charged a reasonable, cost&#45;based fee
                for each subsequent accounting you request within the same 12&#45;month period.</>,
            },
            {
              bulletText: <><strong>Restrictions on Use and Disclosure of Your Protected Health Information</strong>&#8212;You have the right
                to request restrictions on certain of our uses and disclosures of your protected health information for
                payment or health care operations. Your request must describe in detail the restriction you are requesting.
                We are not required to agree to your request for a restriction.</>,
            },
            {
              bulletText: <><strong>Requesting Confidential Communication of Your Protected Health Information</strong>&#8212;You have the
                right to request that communications regarding your protected health information from us be delivered by
                alternative means or at alternative locations. We will accommodate reasonable requests, such as instructions
                that messages not be left on voice mail or sent to a particular address.</>,
            },
            {
              bulletText: <><strong>Right to Notification Following a Breach of Unsecured Protected Health Information</strong>&#8212;You will
                receive notifications from the Company of breaches of your unsecured protected health information.</>,
            },
          ]}
        />
        <Typography
          component='h3'
          align='center'
          nextElement='typography'>
          Complaints
        </Typography>
        <Typography nextElement='typography'>
          If you believe your privacy rights have been violated, you can file a complaint in writing with our Privacy
          Official at the address listed at the end of this Notice. You may also file a complaint in writing with the
          Secretary of the U.S. Department of Health and Human Services in Washington D.C. within 180 days of a violation
          of your rights. There will be no retaliation for filing a complaint.
        </Typography>
        <Typography
          component='h3'
          align='center'
          nextElement='typography'>
          For Further Information
        </Typography>
        <Typography nextElement='typography'>
          If you have questions, wish to request a paper copy of this Notice, or need further information regarding this
          Notice, you may do so by directing your inquiries to:
        </Typography>
        <Typography nextElement='typography'>
          Privacy Official
        </Typography>
        <Typography nextElement='typography'>
          Northwestern Long Term Care Insurance Company
        </Typography>
        <Typography nextElement='typography'>
          720 East Wisconsin Avenue
        </Typography>
        <Typography nextElement='link'>
          Milwaukee, WI 53202
        </Typography>
        <LinkStyled
          id='hippa-privacy-notice-ltc-privacy-official-phone-number-link'
          variant='primaryDeemphasize'
          linkUrl='tel:4146612510'>
          414&#45;661&#45;2510
        </LinkStyled>
        <Typography
          component='h3'
          align='center'
          nextElement='typography'>
          Effective Date
        </Typography>
        <Typography nextElement='typography'>
          This Notice of Privacy Practices is effective April 4, 2013.
        </Typography>
        <Typography nextElement='typography'>
          <strong>&#42;Northwestern Mutual Financial Representatives are individuals authorized to sell for Northwestern Long
            Term Care Insurance Co.</strong>
        </Typography>
        <Typography disableBottomPadding>
          Northwestern Mutual is the marketing name for The Northwestern Mutual Life Insurance Company and its
          subsidiaries. Life and disability insurance, annuities, and life insurance with long&#45;term care benefits are
          issued by The Northwestern Mutual Life Insurance Company, Milwaukee, WI &#40;NM&#41;. Long&#45;term care insurance is issued
          by Northwestern Long Term Care Insurance Company, Milwaukee, WI, &#40;NLTC&#41; a subsidiary of NM. Securities are
          offered through Northwestern Mutual Investment Services, LLC, &#40;NMIS&#41; a subsidiary of NM, broker&#45;dealer,
          registered investment adviser, member&#160;
          <Link
            id='hipaa-privacy-notice-ltc-finra-link'
            variant='primaryDeemphasize'
            isOutgoingLink
            linkUrl='https://www.finra.org/'>
            FINRA
          </Link>
          &#160;and&#160;
          <Link
            id='hipaa-privacy-notice-ltc-sipc-link'
            variant='primaryDeemphasize'
            isOutgoingLink
            linkUrl='https://www.sipc.org/'>
            SIPC
          </Link>
          . Fiduciary and fee&#45;based financial planning services are offered through Northwestern Mutual Wealth Management Company&#174;
          &#40;NMWMC&#41;, Milwaukee, WI, a subsidiary of NM and a federal savings bank. Products and services referenced are offered and sold only
          by appropriately appointed and licensed entities and financial advisors. Not all products and services are available in all states.
        </Typography>
      </Col>
    </Row>
  </SectionContainer>
);
